import { useMemo } from "react";
import { useSelector } from "store";
import useSWR from "swr";
import { fetchFirestoreDocument } from "utility/firebase";

import type { VideoData, VideoGroupData, VideoGroupsSetData } from "./types";
import type {
  EventVideoGroupsSet,
  EventVideoGroup,
  EventVideo,
} from "@spwn/types/firebase/firestore";
import type { Cookie } from "@spwn/types/functions";
import type { Store } from "store";

type DocumentRefLike = {
  path: string;
  id: string;
};

const fetchVideoGroups = async (
  videoGroupRefs: DocumentRefLike[]
): Promise<Record<string, EventVideoGroup>> => {
  const results = await Promise.all(
    videoGroupRefs.map(async (videoGroupRef) => {
      try {
        /**
         * TODO: zodを使用してruntimeにバリデーションを行うようにする
         */
        const videoGroup = await fetchFirestoreDocument<EventVideoGroup>(
          videoGroupRef.path
        );
        return { id: videoGroupRef.id, videoGroup };
      } catch (error) {
        console.error(`Failed to fetch videoGroup ${videoGroupRef.id}:`, error);
        return { id: videoGroupRef.id, videoGroup: null };
      }
    })
  );

  const groups: Record<string, EventVideoGroup> = {};
  for (const { id, videoGroup } of results) {
    if (videoGroup) {
      groups[id] = videoGroup;
    }
  }
  return groups;
};

const fetchVideoGroupsSets = async (
  videoGroupsSetRefs: DocumentRefLike[]
): Promise<Record<string, EventVideoGroupsSet>> => {
  const results = await Promise.all(
    videoGroupsSetRefs.map(async (videoGroupsSetRef) => {
      try {
        /**
         * TODO: zodを使用してruntimeにバリデーションを行うようにする
         */
        const videoGroupsSet =
          await fetchFirestoreDocument<EventVideoGroupsSet>(
            videoGroupsSetRef.path
          );
        return { id: videoGroupsSetRef.id, videoGroupsSet };
      } catch (error) {
        console.error(
          `Failed to fetch videoGroupsSet ${videoGroupsSetRef.id}:`,
          error
        );
        return { id: videoGroupsSetRef.id, videoGroupsSet: null };
      }
    })
  );

  const sets: Record<string, EventVideoGroupsSet> = {};
  for (const { id, videoGroupsSet } of results) {
    if (videoGroupsSet) {
      sets[id] = videoGroupsSet;
    }
  }
  return sets;
};

const useGetEventVideoGroups = (
  eventVideoMap: Record<string, EventVideo> | null
) => {
  const videoGroupRefs = useMemo(() => {
    if (eventVideoMap === null) return [];
    const refMap = new Map<string, NonNullable<EventVideo["videoGroupRef"]>>();
    for (const video of Object.values(eventVideoMap)) {
      const ref = video.videoGroupRef;
      if (ref !== undefined && !refMap.has(ref.path)) {
        refMap.set(ref.path, ref);
      }
    }
    return Array.from(refMap.values());
  }, [eventVideoMap]);

  const videoGroupRefPaths = useMemo(
    () =>
      videoGroupRefs
        .map((ref) => ref.path)
        .sort()
        .join(","),
    [videoGroupRefs]
  );

  return useSWR(
    videoGroupRefs.length > 0 ? ["videoGroups", videoGroupRefPaths] : null,
    async () => fetchVideoGroups(videoGroupRefs)
  );
};

const useGetEventVideoGroupsSets = (
  videoGroups: Record<string, EventVideoGroup>
) => {
  const videoGroupsSetRefs = useMemo(() => {
    const refMap = new Map<string, EventVideoGroup["videoGroupsSetRef"]>();
    for (const group of Object.values(videoGroups)) {
      const ref = group.videoGroupsSetRef;
      if (!refMap.has(ref.path)) {
        refMap.set(ref.path, ref);
      }
    }
    return Array.from(refMap.values());
  }, [videoGroups]);

  const videoGroupsSetRefPaths = useMemo(
    () =>
      videoGroupsSetRefs
        .map((ref) => ref.path)
        .sort()
        .join(","),
    [videoGroupsSetRefs]
  );

  return useSWR(
    videoGroupsSetRefs.length > 0
      ? ["videoGroupsSets", videoGroupsSetRefPaths]
      : null,
    async () => fetchVideoGroupsSets(videoGroupsSetRefs)
  );
};

const constructVideoGroupsSet = ({
  videoGroups,
  videoGroupsSets,
  videoMap,
  currentVideo,
  streamingKey,
}: {
  videoGroups: Record<string, EventVideoGroup>;
  videoGroupsSets: Record<string, EventVideoGroupsSet>;
  videoMap: Record<string, EventVideo> | null;
  currentVideo: EventVideo;
  streamingKey: {
    cookies?: Record<string, { default: Cookie }>;
  } | null;
}): VideoGroupsSetData | undefined => {
  if (videoMap === null) return undefined;

  // currentVideoのvideoGroupIdを取得
  const currentVideoGroupId = currentVideo.videoGroupRef?.id;
  if (!currentVideoGroupId) return undefined;

  // 該当のvideoGroupを取得
  const currentVideoGroup = videoGroups[currentVideoGroupId];
  if (!currentVideoGroup) return undefined;

  // 該当のvideoGroupsSetを取得
  const videoGroupsSetId = currentVideoGroup.videoGroupsSetRef.id;
  const videoGroupsSet = videoGroupsSets[videoGroupsSetId];
  if (!videoGroupsSet) return undefined;

  // 同じvideoGroupsSetIdを持つ全てのvideoGroupを抽出
  const videoGroupsInSet: VideoGroupData[] = Object.entries(videoGroups)
    .filter(([_, group]) => group.videoGroupsSetRef.id === videoGroupsSetId)
    .map(([videoGroupId, group]) => {
      // このvideoGroupに属するvideoを抽出
      const videosInGroup: VideoData[] = Object.entries(videoMap)
        .filter(([_, video]) => video.videoGroupRef?.id === videoGroupId)
        .map(([videoId, video]) => {
          const cookie = streamingKey?.cookies?.[videoId]?.default;
          const url = cookie?.url;

          return {
            name: video.name,
            videoId,
            priority: video.priority,
            url,
          };
        })
        .sort((a, b) => a.priority - b.priority);

      return {
        name: group.name,
        priority: group.priority,
        videos: videosInGroup,
      };
    })
    .sort((a, b) => a.priority - b.priority); // priorityでソート

  return {
    name: videoGroupsSet.name,
    videoGroups: videoGroupsInSet,
  };
};

export const useVideoGroupsSet = (
  currentVideoId: string | null
): VideoGroupsSetData | undefined => {
  const eventVideoMap = useSelector(
    (state: Store) => state.streaming.eventVideoMap
  );
  const streamingKey = useSelector((state) => state.streaming.streamingKey);

  const currentVideo = currentVideoId
    ? eventVideoMap?.[currentVideoId]
    : undefined;

  const { data: videoGroups = {} } = useGetEventVideoGroups(eventVideoMap);
  const { data: videoGroupsSets = {} } =
    useGetEventVideoGroupsSets(videoGroups);

  const videoGroupsSet = useMemo(() => {
    if (currentVideo === undefined) {
      return undefined;
    }
    return constructVideoGroupsSet({
      videoGroups,
      videoGroupsSets,
      videoMap: eventVideoMap,
      currentVideo,
      streamingKey,
    });
  }, [videoGroups, videoGroupsSets, eventVideoMap, currentVideo, streamingKey]);

  return videoGroupsSet;
};
